import { useEffect, useMemo, useState } from 'react';
import { Alert, Box, Drawer, TextField, Typography } from '@mui/material';

import PrimaryBlueButton from '../../../../../components/common/buttons/PrimaryBlueButton';
import BorderedButton from '../../../../../components/common/buttons/BorderedButton';
import { validateZipcode } from '../../../../../utils/validations';
import { AccountLocation } from '../../../../../types/account';

export type AccountLocationDrawerMode = 'create' | 'edit';

export type LocationDrawerPayload = {
  name: string;
  description?: string | null;
  address_line_1?: string | null;
  address_line_2?: string | null;
  address_city?: string | null;
  address_state?: string | null;
  address_zip?: string | null;
};

type Props = {
  open: boolean;
  mode: AccountLocationDrawerMode;
  initialLocation?: AccountLocation | null;
  onClose: () => void;
  onSubmit: (payload: LocationDrawerPayload) => Promise<void> | void;
  isSubmitting?: boolean;
};

type FieldErrors = Partial<Record<keyof LocationDrawerPayload, string>>;

const trimOrNull = (v: string): string | null => {
  const t = v.trim();
  return t ? t : null;
};

const trim = (v: string): string => v.trim();

export default function LocationDrawer({
  open,
  mode,
  initialLocation,
  onClose,
  onSubmit,
  isSubmitting = false,
}: Props): JSX.Element {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');

  const [addressLine1, setAddressLine1] = useState('');
  const [addressLine2, setAddressLine2] = useState('');
  const [city, setCity] = useState('');
  const [stateVal, setStateVal] = useState('');
  const [zip, setZip] = useState('');

  const [errors, setErrors] = useState<FieldErrors>({});
  const [formError, setFormError] = useState<string | null>(null);

  const reset = () => {
    setName('');
    setDescription('');
    setAddressLine1('');
    setAddressLine2('');
    setCity('');
    setStateVal('');
    setZip('');
    setErrors({});
    setFormError(null);
  };

  useEffect(() => {
    if (!open) {
      reset();
      return;
    }

    if (mode === 'edit' && initialLocation) {
      setName(initialLocation.name ?? '');
      setDescription(initialLocation.description ?? '');
      setAddressLine1(initialLocation.address_line_1 ?? '');
      setAddressLine2(initialLocation.address_line_2 ?? '');
      setCity(initialLocation.address_city ?? '');
      setStateVal(initialLocation.address_state ?? '');
      setZip(initialLocation.address_zip ?? '');
      setErrors({});
      setFormError(null);
      return;
    }

    reset();
  }, [open, mode, initialLocation]);

  const payload: LocationDrawerPayload = useMemo(
    () => ({
      name: name.trim(),
      description: trimOrNull(description),
      address_line_1: trimOrNull(addressLine1),
      address_line_2: trimOrNull(addressLine2),
      address_city: trimOrNull(city),
      address_state: trimOrNull(stateVal),
      address_zip: trimOrNull(zip),
    }),
    [addressLine1, addressLine2, city, description, name, stateVal, zip],
  );

  const validate = (): boolean => {
    const next: FieldErrors = {};

    const locationName = trim(name);
    const addressLine1Value = trim(addressLine1);
    const cityValue = trim(city);
    const stateValue = trim(stateVal);
    const zipValue = trim(zip);

    if (!locationName) next.name = 'Location name is required.';
    if (locationName && locationName.length > 120) next.name = 'Location name is too long. (max 120 characters)';

    if (!addressLine1Value) next.address_line_1 = 'Address line 1 is required.';
    if (!cityValue) next.address_city = 'City is required.';
    if (!stateValue) next.address_state = 'State is required.';

    if (zipValue) {
      const zipErr = validateZipcode(zipValue);
      if (zipErr) next.address_zip = zipErr;
    }

    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = async () => {
    setFormError(null);
    if (!validate()) return;

    try {
      await onSubmit(payload);
    } catch {
      setFormError(
        mode === 'edit'
          ? 'Unable to update location right now. Please try again.'
          : 'Unable to create location right now. Please try again.',
      );
    }
  };

  const title = mode === 'edit' ? 'Edit Location' : 'Add Location';
  const primaryText = mode === 'edit' ? (isSubmitting ? 'Saving...' : 'Save') : isSubmitting ? 'Creating...' : 'Create';

  return (
    <Drawer anchor="right" open={open} onClose={isSubmitting ? undefined : onClose}>
      <Box sx={{ width: { xs: 360, sm: 440 }, p: 3 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>
          {title}
        </Typography>

        {formError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {formError}
          </Alert>
        )}

        <TextField
          label="Location Name"
          value={name}
          onChange={(e) => {
            setName(e.target.value);
            setErrors((prev) => ({ ...prev, name: undefined }));
            setFormError(null);
          }}
          fullWidth
          required
          error={!!errors.name}
          helperText={errors.name}
          sx={{ mb: 2 }}
          inputProps={{ maxLength: 120 }}
        />

        <TextField
          label="Description"
          value={description}
          onChange={(e) => {
            setDescription(e.target.value);
            setFormError(null);
          }}
          fullWidth
          multiline
          minRows={2}
          sx={{ mb: 2 }}
        />

        <Typography variant="subtitle2" sx={{ mt: 1, mb: 1 }}>
          Address
        </Typography>

        <TextField
          label="Address line 1"
          value={addressLine1}
          onChange={(e) => {
            setAddressLine1(e.target.value);
            setErrors((prev) => ({ ...prev, address_line_1: undefined }));
            setFormError(null);
          }}
          fullWidth
          required
          error={!!errors.address_line_1}
          helperText={errors.address_line_1}
          sx={{ mb: 2 }}
        />

        <TextField
          label="Address line 2"
          value={addressLine2}
          onChange={(e) => {
            setAddressLine2(e.target.value);
            setFormError(null);
          }}
          fullWidth
          sx={{ mb: 2 }}
        />

        <TextField
          label="City"
          value={city}
          onChange={(e) => {
            setCity(e.target.value);
            setErrors((prev) => ({ ...prev, address_city: undefined }));
            setFormError(null);
          }}
          fullWidth
          required
          error={!!errors.address_city}
          helperText={errors.address_city}
          sx={{ mb: 2 }}
        />

        <TextField
          label="State"
          value={stateVal}
          onChange={(e) => {
            setStateVal(e.target.value);
            setErrors((prev) => ({ ...prev, address_state: undefined }));
            setFormError(null);
          }}
          fullWidth
          required
          error={!!errors.address_state}
          helperText={errors.address_state}
          sx={{ mb: 2 }}
        />

        <TextField
          label="ZIP / Postal code"
          value={zip}
          onChange={(e) => {
            setZip(e.target.value);
            setErrors((prev) => ({ ...prev, address_zip: undefined }));
            setFormError(null);
          }}
          fullWidth
          error={!!errors.address_zip}
          helperText={errors.address_zip}
          sx={{ mb: 3 }}
        />

        <Box sx={{ display: 'flex', gap: 2 }}>
          <BorderedButton
            onClick={onClose}
            sxProps={{ width: '100%', height: 40 }}
            buttonText="Cancel"
            additionalProps={{ disabled: isSubmitting }}
          />
          <PrimaryBlueButton
            onClick={handleSubmit}
            sxProps={{ width: '100%', height: 40 }}
            buttonText={primaryText}
            additionalProps={{ disabled: isSubmitting }}
          />
        </Box>
      </Box>
    </Drawer>
  );
}
